import { Link } from 'react-router-dom';
import type { Comic } from '@/types/content';
import { useFavorites } from '@/hooks/useFavorites';

interface ComicCardProps {
  comic: Comic;
  layout?: 'grid' | 'row';
}

export default function ComicCard({ comic, layout = 'grid' }: ComicCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(comic.id);

  const handleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(comic.id);
  };

  if (layout === 'row') {
    return (
      <Link
        to={`/detail/comics/${comic.id}`}
        className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
      >
        <div className="h-36 w-[108px] shrink-0 overflow-hidden rounded-md bg-background-200">
          <img src={comic.cover} alt={`${comic.title}漫画封面`} className="h-full w-full object-cover object-top" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate font-heading text-base font-700 text-foreground-950 group-hover:text-primary-700">
              {comic.title}
            </h3>
            <button
              type="button"
              onClick={handleFavorite}
              aria-label="收藏"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100"
            >
              <i className={favorite ? 'ri-heart-3-fill text-accent-500' : 'ri-heart-3-line'} />
            </button>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-label font-500 text-primary-800">{comic.genre}</span>
            <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-label font-500 text-secondary-800">{comic.status}</span>
            <span className="text-xs font-label text-foreground-500">{comic.region}</span>
          </div>
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{comic.summary}</p>
          <div className="mt-2 flex items-center gap-4 text-xs font-label text-foreground-500">
            <span>{comic.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500" />
              {comic.rating}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-line" />
              {(comic.favorites / 1000).toFixed(1)}k
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/comics/${comic.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={comic.cover}
          alt={`${comic.title}漫画封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2.5 top-2.5 rounded-md bg-primary-500 px-2 py-0.5 text-xs font-label font-500 text-background-50">
          {comic.status}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label="收藏"
          className="absolute right-2.5 top-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-base text-foreground-600 backdrop-blur transition-colors hover:text-accent-600"
        >
          <i className={favorite ? 'ri-heart-3-fill text-accent-500' : 'ri-heart-3-line'} />
        </button>
        <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs font-label text-background-50 backdrop-blur">
          <i className="ri-star-fill text-accent-400" />
          {comic.rating}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3">
        <h3 className="truncate font-heading text-base font-700 text-foreground-950 group-hover:text-primary-700">
          {comic.title}
        </h3>
        <p className="mt-1 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600">{comic.summary}</p>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {comic.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-label font-500 text-accent-900">
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between border-t border-background-200 pt-2 text-xs font-label text-foreground-500">
          <span className="truncate">{comic.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-3-line" />
            {(comic.favorites / 1000).toFixed(1)}k
          </span>
        </div>
      </div>
    </Link>
  );
}